import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useClub } from "../club/ClubContext";
import { httpClient } from "../httpClient";
import { useNotify } from "react-admin";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Button,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";

type SessionListItem = {
  id: number;
  template_id: number;
  status: "in_progress" | "finished" | "aborted";
  actual_points_total: number;
  started_at: string;
  finished_at?: string | null;
};

function formatDt(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function TrainingStartPage() {
  const { templateId } = useParams();
  const { clubId } = useClub();
  const nav = useNavigate();

  const tplId = Number(templateId);

  const [sessions, setSessions] = React.useState<SessionListItem[]>([]);
  const [loading, setLoading] = React.useState(true);

  // Delete dialog state
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [sessionToDelete, setSessionToDelete] = React.useState<SessionListItem | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const loadSessions = React.useCallback(async () => {
    if (!clubId || !tplId) return;
    setLoading(true);
    try {
      const { json } = await httpClient(
        `/api/training/my-sessions?club_id=${clubId}&template_id=${tplId}&limit=20`
      );
      setSessions(json as SessionListItem[]);
    } finally {
      setLoading(false);
    }
  }, [clubId, tplId]);

  React.useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const openSession = React.useMemo(
    () => sessions.find((s) => s.status === "in_progress") ?? null,
    [sessions]
  );

  const onStartNew = async () => {
    const { json } = await httpClient(`/api/training/sessions?club_id=${clubId}`, {
      method: "POST",
      body: JSON.stringify({ template_id: tplId }),
    });
    nav(`/play/session/${json.id}`);
  };

  const onResume = () => {
    if (!openSession) return;
    nav(`/play/session/${openSession.id}`);
  };

  const askDelete = (s: SessionListItem) => {
    setSessionToDelete(s);
    setConfirmOpen(true);
  };

  const notify = useNotify();

const doDelete = async () => {
  if (!sessionToDelete) return;
  setDeleting(true);

  try {
    const res = await httpClient(
      `/api/training/sessions/${sessionToDelete.id}?club_id=${clubId}`,
      { method: "DELETE" }
    );

    notify(
      res?.json?.detail ?? res?.json?.message ?? "Session gelöscht.",
      { type: "info" }
    );

    setConfirmOpen(false);
    setSessionToDelete(null);
    await loadSessions();
  } catch (e: any) {
    notify(
      e?.body?.detail ?? e?.body?.message ?? e?.message ?? "Löschen fehlgeschlagen.",
      { type: "warning" }
    );
  } finally {
    setDeleting(false);
  }
};


  const statusLabel = (s: SessionListItem) =>
    s.status === "in_progress" ? "Offen" : s.status === "finished" ? "Beendet" : "Abgebrochen";

  return (
    <>
      <Box mb={2}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => nav(-1)}>
          Zurück
        </Button>
      </Box>

      <Stack spacing={2} sx={{ p: 2, width: "100%" }}>
        <Typography variant="h5" fontWeight={700}>
          Training Übersicht
        </Typography>

        {/* Offenes Training */}
        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            <Stack spacing={1.5}>
              {openSession ? (
                <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={800}>Offenes Training gefunden</Typography>

                    <Tooltip title="Offenes Training löschen">
                      <span>
                        <IconButton
                          color="error"
                          onClick={() => askDelete(openSession)}
                          disabled={deleting}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>

                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Chip size="small" color="primary" label="in Bearbeitung" />
                    <Chip size="small" label={`Start: ${formatDt(openSession.started_at)}`} />
                    <Chip size="small" label={`Punkte: ${openSession.actual_points_total}`} />
                  </Stack>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ borderRadius: 3, py: 1.2, fontWeight: 900 }}
                    onClick={onResume}
                  >
                    Fortsetzen
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ borderRadius: 3, py: 1.2 }}
                    onClick={onStartNew}
                  >
                    Neues Training starten
                  </Button>
                </>
              ) : (
                <>
                  <Typography fontWeight={800}>Keine offenes Training</Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ borderRadius: 3, py: 1.2, fontWeight: 900 }}
                    onClick={onStartNew}
                  >
                    Neues Training starten
                  </Button>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Letzte Trainings */}
        <Card sx={{ borderRadius: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={800}>
              Letzte Trainings
            </Typography>
            <Divider sx={{ my: 2 }} />

            {loading ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Lade…
              </Typography>
            ) : sessions.length === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Noch kein Training gespielt.
              </Typography>
            ) : (
              <Stack spacing={1.2}>
                {sessions.map((s) => (
                  <Card key={s.id} variant="outlined" sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack spacing={0.3}>
                          <Typography fontWeight={800}>{statusLabel(s)}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            Start: {formatDt(s.started_at)}
                            {s.finished_at ? ` · Ende: ${formatDt(s.finished_at)}` : ""}
                          </Typography>
                        </Stack>

                        <Stack alignItems="flex-end" spacing={0.6}>
                          <Chip size="small" label={`${s.actual_points_total} Punkte`} />

                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant={s.status === "in_progress" ? "contained" : "outlined"}
                              sx={{ borderRadius: 1 }}
                              onClick={() => nav(`/play/session/${s.id}`)}
                            >
                              {s.status === "in_progress" ? "Fortsetzen" : "Ansehen"}
                            </Button>
                          </Stack>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => (deleting ? null : setConfirmOpen(false))}>
        <DialogTitle>Training löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Willst du diese Session wirklich löschen? Das kann nicht rückgängig gemacht werden.
            <br />
            <b>Session #{sessionToDelete?.id}</b>
            {sessionToDelete?.status === "in_progress" ? " (offen)" : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Abbrechen
          </Button>
          <Button onClick={doDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? "Lösche…" : "Löschen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
